// src/pages/PasswordRecovery/RecoverySetPassword.tsx
import React from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from '@tanstack/react-router';
import { Card, Container, HeaderText, BodyText, Form } from 'shared-portal-component';
import { z } from 'zod';

import TwoSlot from '@/components/molecules/TwoSlot/TwoSlot';
import { useAppDispatch } from '@/store';
import { setPasswordThunk } from '@/store/thunks/auth/setPasswordThunk';
import { selectForgotPasswordEmailAddress } from '@/store/selectors/auth';

const MIN_CHARACTER_NUMBER = 12;

const schema = z
  .object({
    password: z
      .string()
      .min(MIN_CHARACTER_NUMBER, 'Password must be at least 12 characters long')
      .regex(/[A-Z]/, 'Must contain at least 1 uppercase letter')
      .regex(/[a-z]/, 'Must contain at least 1 lowercase letter')
      .regex(/\d/, 'Must contain at least 1 number')
      .regex(/[^A-Za-z0-9]/, 'Must contain at least 1 special character'),
    confirmPassword: z.string(),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: 'Passwords do not match',
    path: ['confirmPassword'],
  });

type FormValues = z.infer<typeof schema>;

const leftRequirements = [
  'At least 12 characters long',
  'At least 1 uppercase letter',
  'At least 1 lowercase letter',
];

const rightRequirements = ['At least 1 number', 'At least 1 special character'];

const fields = [
  {
    name: 'password',
    label: 'Password',
    placeholder: 'Enter a new password',
    required: true,
    variant: 'outline' as const,
    type: 'password',
  },
  {
    name: 'confirmPassword',
    label: 'Confirm Password',
    placeholder: 'Re-enter your password to confirm it',
    required: true,
    variant: 'outline' as const,
    type: 'password',
  },
];

export default function RecoverySetPasswordPage(): JSX.Element {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const username = useSelector(selectForgotPasswordEmailAddress);

  const handleSubmit = React.useCallback(
    async (values: FormValues): Promise<void> => {
      if(username === undefined) return
      const body = { username, password: values.password };

      const action = await dispatch(setPasswordThunk(body));
      if (setPasswordThunk.fulfilled.match(action)) {
        void navigate({ to: '/login' });
      }
    },
    [dispatch, navigate, username]
  );

  return (
    <Container fluid>
      <Card>
        <HeaderText text="Reset your Password" size="h2" className="header-text-auth" />
        <BodyText
          text="Please reset and confirm a password for your administrator account. Passwords must be:"
          color="#000"
          className="body-text-auth"
          as="p"
        />

        <TwoSlot>
          <TwoSlot.FirstSlot>
            {leftRequirements.map((requirement) => (
              <BodyText as="p" text={requirement} key={requirement} />
            ))}
          </TwoSlot.FirstSlot>
          <TwoSlot.SecondSlot>
            {rightRequirements.map((requirement) => (
              <BodyText as="p" text={requirement} key={requirement} />
            ))}
          </TwoSlot.SecondSlot>
        </TwoSlot>

        <Form schema={schema} fields={fields} onSubmit={handleSubmit} />
      </Card>
    </Container>
  );
}
